import React, { Fragment, useMemo, useContext, useEffect, useState } from "react";
import { CartContext } from "components/cart";
import classNames from "lib/classNames";
import HeaderMachine from "../machines/HeaderMachine";
import { useMachine } from "@xstate/react";
import useRefState from "hooks/useRefState";
import useCampaign from "hooks/useCampaign";
import promisify from "lib/promisify";
import { getAllOCUProducts } from "./CheckoutButton";
import Axios from "axios";

export default function HeaderATU ({ add_to_unlock, bundlesDiscounts }) {
  const {
    cart_settings: {
      theme: { primary },
      font: { color },
      rtl,
      money_format
    },
    campaigns,
    setShowConfetti,
    shopify_cart_items,
    Utils,
    add_to_cart,
    update_cart_item,
    get_total,
    productNotAddedToCart,
    productAddedToCart,
    discountCode,
    setCartItems
  } = useContext(CartContext);
  const [__, setContextCache, contextCache] = useRefState(null);
  const [one_check_up_sell_product_id, ocu_campaign] = useCampaign(
    "one_check_up_sell", null, true
  )(campaigns, shopify_cart_items);
  const ocu_products = getAllOCUProducts(ocu_campaign)
  const filteredItems = shopify_cart_items.filter((item) => {
    const ocu_product = ocu_products.find(({ product }) => item.product_id.toString() === product?.id)
    return ocu_product ? ocu_product.include_in_atu : item?.product_type?.toLowerCase() !== "free_gift"
  });
  const { original, subtotal } = get_total(null, filteredItems);
  const byQuantity = add_to_unlock.multi_goal_type === "quantity"
  const headerMachine = useMemo(
    () =>
      HeaderMachine({
        goals: add_to_unlock.goals,
        setShowConfetti,
        shopify_cart_items: filteredItems,
        original,
        subtotal,
        bundlesDiscounts,
        byQuantity,
        contextCache: contextCache.current
      }),
    [shopify_cart_items, add_to_unlock, bundlesDiscounts, contextCache.current]
  );
  const [current, send, service] = useMachine(headerMachine);
  const { context } = headerMachine;
  const progressWidth = 310 / context.goals.length;

  useEffect(() => {
    const goals = context.goals.filter(({ goalObject }) => goalObject.reward_type === "gift")
    if (goals.length) {
      handleGifts({ goals, discountCode, productNotAddedToCart, productAddedToCart, setCartItems })
    }
  }, [discountCode, shopify_cart_items])

  useEffect(() => {
    if (JSON.stringify(context) !== JSON.stringify(contextCache.current)) {
      if (contextCache.current) {
        const showConfettiPreview = context?.goals?.filter(
          ({ goalObject: { confetti } }, i) =>
            confetti && !contextCache?.current?.goals[i]?.goalObject?.confetti
        );
        if (showConfettiPreview.length) {
          setShowConfetti(showConfettiPreview[0].goalObject.confetti_length);
        }
      }
      setContextCache(context);
    }
  }, [context]);

  return (
    <div
      className={classNames("px-5 py-2 mb-px shadow atu-banner")}
      data-offset={1}
      style={{ background: add_to_unlock.progress_background ? Utils.formatColor(add_to_unlock.progress_background) : "inherit" }}
    >
      {context.goals.map(
        ({ textKey, goalObject, unlocked, previousIsUnlocked, isFirst, isLast }, index) => (
          <GoalText
            key={textKey}
            unlocked={unlocked}
            bundlesDiscounts={bundlesDiscounts}
            currentGoal={goalObject}
            add_to_unlock={add_to_unlock}
            filteredItems={filteredItems}
            otherUnlockedDiscounts={context.goals.filter(
              ({ unlocked, goalObject: { reward_type } }, i) =>
                i !== index && unlocked && reward_type === "discount"
            )}
            previousIsUnlocked={previousIsUnlocked}
            show_all_locked_goals={add_to_unlock.show_all_locked_goals}
            {...{ isFirst, isLast }}
          />
        )
      )}
      <div
        className={classNames(
          "flex w-full h-2 rounded relative overflow-hidden mix-blend-darken"
        )}
        style={{
          background: Utils.formatColor(add_to_unlock.progress_secondary)
        }}
      >
        <div
          className={classNames(
            "block transition-width absolute inset-0 duration-500 ease-out z-10"
          )}
          style={{
            background:
              context.goals.filter(({ unlocked }) => !unlocked).length > 0
                ? Utils.formatColor(add_to_unlock.progress_primary)
                : Utils.formatColor(add_to_unlock.progress_complete),
            width: `${context.goals.reduce((acc, cur) => {
              const { unlocked, isFirst, previousIsUnlocked, value } = cur;
              return isFirst || previousIsUnlocked ? acc + value : acc;
            }, 0) / context.goals.length
              }%`
          }}
        />
        {context.goals.map(({ key, isFirst, isLast }, index) => (
          <ProgressBar
            key={key}
            rtl={rtl}
            isFirst={isFirst}
            isLast={isLast}
            borderColor="#718096"
          />
        ))}
      </div>
      {/* {context.goals.length > 1 && ( */}
      <div
        className={classNames(
          "flex justify-between text-gray-600 relative h-3"
        )}
      >
        {context.goals.map(
          (
            { labelKey, goalObject: { goal_text, show_goal_text }, isLast },
            index
          ) => {
            if (!show_goal_text) return null;
            return (
              <div
                key={labelKey}
                className={classNames("absolute transform w-max")}
                style={{
                  left:
                    !rtl && `${((index + 1) / context.goals.length) * 100}%`,
                  right:
                    rtl && `${((index + 1) / context.goals.length) * 100}%`,
                  width: "fit-content",
                  whiteSpace: "nowrap",
                  fontSize: Utils.em(goal_text.font_size),
                  color: Utils.formatColor(goal_text.color),
                  "--transform-translate-x": `${(isLast ? 100 : 50) * (rtl ? 1 : -1)}%`
                }}
              >
                {goal_text.text}
              </div>
            );
          }
        )}
      </div>
      {/* )} */}
    </div >
  );
}

const ProgressBar = ({ rtl, isFirst, isLast, borderColor }) => {
  return (
    <div
      className={classNames(
        "flex-1 h-2 bg-transparent z-20 mix-blend-overlay",
        {
          "border-r": !rtl && !isLast,
          "border-l": rtl && !isLast
        }
      )}
      style={{ borderColor }}
    />
  );
};

const textIsEmpty = (html) => {
  const element = document.createElement("span");
  element.innerHTML = html;
  return element.innerText.length === 0;
};

const GoalText = ({
  currentGoal,
  add_to_unlock: { multi_goal_type },
  unlocked,
  otherUnlockedDiscounts,
  previousIsUnlocked,
  show_all_locked_goals,
  bundlesDiscounts,
  filteredItems,
  isLast,
  isFirst
}) => {
  const {
    cart_settings: {
      font: { color },
      money_format
    },
    Utils,
    get_total
  } = useContext(CartContext);
  const { goal, reward_type } = currentGoal;
  const reward = currentGoal[reward_type];
  const isDiscount = reward_type === "discount";
  const notTheHighestDiscount = isDiscount
    ? !!otherUnlockedDiscounts.find(
      ({ goalObject: { discount } }) => discount.reward > reward.reward
    )
    : false;
  const text = unlocked ? reward.after : reward.before;
  // if (
  //   textIsEmpty(text) ||
  //   notTheHighestDiscount ||
  //   previousIsUnlocked === false ||
  //   (!isLast && !show_all_locked_goals)
  // )
  //   return null;
  if (!textIsEmpty(text) && !notTheHighestDiscount && (
    (show_all_locked_goals && unlocked) ||
    (isFirst && !unlocked) ||
    (isLast && unlocked) ||
    (!unlocked && previousIsUnlocked))
  )
    //   if(!textIsEmpty(text) && !notTheHighestDiscount && 
    //   ((isFirst && ((unlocked && show_all_locked_goals) || !unlocked) ) || 
    //   (isLast && (unlocked || (!unlocked && previousIsUnlocked)))) ||
    //   (!isLast && !isFirst && ((unlocked && show_all_locked_goals) || (!unlocked && previousIsUnlocked)))
    // )
    return (
      <div
        className={classNames("emoji-image mb-1 text-center")}
        style={{ color }}
        dangerouslySetInnerHTML={{
          __html: formateText(
            text,
            reward_type,
            reward,
            (v) => Utils.formatMoney(v, money_format),
            get_total,
            goal,
            multi_goal_type,
            filteredItems,
            bundlesDiscounts
          )
        }}
      ></div>
    )
  else
    return null
};

const formateText = (
  text,
  rewardType,
  { type, reward },
  formatMoney,
  get_total,
  goal,
  multi_goal_type,
  filteredItems,
  bundlesDiscounts
) => {
  let replacedReward =
    rewardType === "discount"
      ? type === "percentage"
        ? `${reward}%`
        : formatMoney(reward * 100)
      : "";
  const formattedGoal = parseInt(goal * 100 * parseFloat(window.Shopify.currency.rate))
  const { subtotal, original } = get_total(null, filteredItems);
  const currentAmount =
    rewardType === "free_shipping" ? subtotal : original - bundlesDiscounts;
  let replacedAmount = formatMoney(
    (formattedGoal - currentAmount) //* parseFloat(window.Shopify.currency.rate)
  );
  let replacedQuantity =
    goal -
    filteredItems
      .map((item) => item.quantity)
      .reduce((acc, item) => acc + item, 0);

  return text
    .replace(
      "{{amount_left}}",
      multi_goal_type === "quantity" ? replacedQuantity : replacedAmount
    )
    .replace("{{reward}}", replacedReward)
    .replace("{{goal}}", formatMoney(goal * 100));
};

async function handleGifts ({ goals, discountCode, productNotAddedToCart, productAddedToCart, setCartItems }) {
  const { add, remove } = goals.reduce((acc, goal) => {
    const product = goal.goalObject.gift.product
    const { origin } = window.location;
    const action = handleGiftProduct({ goal, discountCode, productNotAddedToCart, productAddedToCart })
    if (action) {
      const id = Number(product.variant_id)
      const product_id = Number(product.id)
      acc[action].push({ id, variant_id: id, product_id, quantity: 1 })
    }
    return acc
  }, { add: [], remove: [] })
  if (add.length) {
    const updates = add.reduce((acc, { id }) => ({ ...acc, [id]: 1 }), {})
    const { data } = await Axios.post(`${origin}/cart/update.js`, { updates })
    if (data?.items) {
      setCartItems(data.items)
    }
  }
  if (remove.length) {
    const updates = remove.reduce((acc, { id }) => ({ ...acc, [id]: 0 }), {})
    const { data } = await Axios.post(`${origin}/cart/update.js`, { updates })
    if (data?.items) {
      setCartItems(data.items)
    }
  }
}
function handleGiftProduct ({ goal, discountCode, productNotAddedToCart, productAddedToCart }) {
  const { goalObject, unlocked } = goal
  const product = goalObject.gift.product
  if (!product) return
  const { variant_id } = product
  if (unlocked && productNotAddedToCart(product) && !discountCode) {
    // await promisify(add_to_cart)({ variant_id })
    return "add"
  }
  else if ((!unlocked || discountCode) && productAddedToCart(product)) {
    return "remove"
    // await promisify(update_cart_item)({ variant_id, quantity: 0 })
  }
  return null
}